import { ClientLoggingService } from '../../../../shared/client-logging.service';
import { DatePipe } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';

import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, takeUntil } from 'rxjs';
import { TabService } from '../../../../shared/tab.service';
import { PlanReviewerService } from '../../../../shared/plan-reviewer.service';
import { ArchitecturalPlanPage } from '../../../../shared/models/architectural-plan-page.model';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

/**
 * Transcript Tab - Displays page.md content
 *
 * Renders the extracted markdown from the PDF as rich text by default,
 * with a toggle to view the raw markdown source (like GitHub's Preview/Code toggle).
 *
 * Related: Issue #1062 - Polish markdown rendering
 */
@Component({
  selector: 'app-raw-markdown',
  imports: [MatButtonToggleModule, MatIconModule, MatTooltipModule, DatePipe],
  template: `
    <div class="transcript-container">
      @if (loading) {
        <div class="loading-indicator">Loading page content...</div>
      }
      @if (error && !loading) {
        <div class="error-state">{{ error }}</div>
      }
      @if (!loading && !error && rawMarkdown) {
        <div class="transcript-toolbar">
          <mat-button-toggle-group
            [value]="viewMode"
            (change)="viewMode = $event.value"
            class="view-toggle"
            hideSingleSelectionIndicator
          >
            <mat-button-toggle value="rendered" matTooltip="Rich text">
              <mat-icon>article</mat-icon>
              <span class="toggle-label">Preview</span>
            </mat-button-toggle>
            <mat-button-toggle value="raw" matTooltip="Raw markdown">
              <mat-icon>code</mat-icon>
              <span class="toggle-label">Markdown</span>
            </mat-button-toggle>
          </mat-button-toggle-group>
        </div>
        @if (viewMode === 'rendered' && renderedHtml) {
          <div class="transcript-content rendered-content markdown-renderer-container" [innerHTML]="renderedHtml"></div>
        }
        @if (viewMode === 'raw') {
          <pre class="transcript-content raw-content">{{ rawMarkdown }}</pre>
        }
      }
      @if (!loading && !error && !rawMarkdown) {
        <div class="empty-state">
          page.md content not available. The file may not have been generated during page ingestion.
        </div>
      }
      @if (activePage?.getModelStamp(); as stamp) {
        @if (stamp.getModelName()) {
          <div class="model-metadata">
            Model: {{ stamp.getModelName() }}
            @if (stamp.getProfileId()) {
              ({{ stamp.getProfileId() }})
            }
            @if (stamp.getCreatedTime()) {
              | {{ modelStampDate | date: 'yyyy/MM/dd HH:mm' }}
            }
          </div>
        }
      }
    </div>
  `,
  styles: [
    `
      .transcript-container {
        min-height: 400px;
      }

      .transcript-toolbar {
        display: flex;
        justify-content: flex-end;
        padding: 12px 24px 0;
      }

      .toggle-label {
        font-size: 12px;
      }

      .transcript-content {
        padding: 24px;
      }

      .rendered-content {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        line-height: 1.6;
        color: #333;
      }

      .raw-content {
        font-family: 'Courier New', monospace;
        font-size: 13px;
        line-height: 1.6;
        background: #f8f9fa;
        border-radius: 6px;
        margin: 0 24px 24px;
        padding: 16px;
        white-space: pre-wrap;
        word-wrap: break-word;
        color: #333;
      }

      .model-metadata {
        font-size: 0.75rem;
        color: rgb(0 0 0 / 54%);
        margin-top: 16px;
        padding: 0 24px 24px;
      }

      .loading-indicator {
        color: #666;
        font-style: italic;
        padding: 24px;
      }

      .error-state {
        color: #d32f2f;
        padding: 24px;
        font-style: italic;
      }

      .empty-state {
        color: #888;
        padding: 24px;
        font-style: italic;
      }
    `,
  ],
})
export class RawMarkdownComponent implements OnInit, OnDestroy {
  private tabService = inject(TabService);
  private planReviewerService = inject(PlanReviewerService);

  activePage: ArchitecturalPlanPage | null = null;
  modelStampDate: Date | null = null;
  rawMarkdown = '';
  renderedHtml = '';
  loading = false;
  error: string | null = null;
  viewMode: 'rendered' | 'raw' = 'rendered';

  private destroy$ = new Subject<void>();
  private fetchAttempted = new Set<string>();

  ngOnInit() {
    this.tabService.activePlanPage$.pipe(takeUntil(this.destroy$)).subscribe(page => {
      this.activePage = page;
      this.modelStampDate = null;
      if (page) {
        const stamp = page.getModelStamp?.();
        const ts = stamp?.getCreatedTime?.();
        if (ts) {
          this.modelStampDate = new Date(ts.getSeconds() * 1000);
        }
        this.loadRawMarkdown();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadRawMarkdown() {
    if (!this.activePage) return;

    this.error = null;

    // Create unique key for this page to prevent infinite loops
    const pageKey = `${this.activePage.getArchitecturalPlanId()}-${this.activePage.getPageFolderPath()}`;

    // Access pageMarkdown from protobuf using getter method
    let pageMarkdown = this.activePage.getPageMarkdown();

    ClientLoggingService.log('INFO', '[RawMarkdown] activePage:', this.activePage);
    ClientLoggingService.log('INFO', '[RawMarkdown] pageMarkdown length:', pageMarkdown.length);

    // Self-healing: Fetch full page content if pageMarkdown is missing
    // Only attempt once per page to prevent infinite loops
    if ((!pageMarkdown || pageMarkdown.length === 0) && !this.fetchAttempted.has(pageKey)) {
      ClientLoggingService.log(
        'INFO',
        '[RawMarkdown] pageMarkdown missing, fetching full page content (triggers backend self-healing)'
      );
      this.fetchAttempted.add(pageKey); // Mark as attempted before making the call
      this.loading = true;

      try {
        // Extract fileId from pageFolderPath
        const pagePath = this.activePage.getPageFolderPath() || '';
        const fileIdMatch = pagePath.match(/\/files\/(\d+)\/pages\/\d+\/?$/);
        const fileId = fileIdMatch ? fileIdMatch[1] : undefined;

        // Fetch full page content (backend will self-heal by reading page.md from GCS)
        const fullPage = await this.planReviewerService.getPageWithFullContent(
          this.activePage.getArchitecturalPlanId(),
          this.activePage.getPageNumber(),
          fileId
        );

        pageMarkdown = fullPage.getPageMarkdown();
        ClientLoggingService.log(
          'INFO',
          '[RawMarkdown] Fetched pageMarkdown via self-healing, length:',
          pageMarkdown.length
        );

        // Only update cache if we got actual content
        if (pageMarkdown && pageMarkdown.length > 0) {
          this.activePage = fullPage;
          const stamp = fullPage.getModelStamp?.();
          const ts = stamp?.getCreatedTime?.();
          if (ts) {
            this.modelStampDate = new Date(ts.getSeconds() * 1000);
          } else {
            this.modelStampDate = null;
          }
          this.tabService.setActivePlanPage(fullPage);
        }
      } catch (fetchError) {
        ClientLoggingService.log('ERROR', '[RawMarkdown] Error fetching full page content:', fetchError);
        this.error = 'Error loading page content. Please try again later.';
        this.loading = false;
        return;
      } finally {
        this.loading = false;
      }
    }

    if (pageMarkdown && pageMarkdown.length > 0) {
      this.rawMarkdown = pageMarkdown;
      try {
        const rawHtml = await marked.parse(pageMarkdown);
        this.renderedHtml = DOMPurify.sanitize(rawHtml);
      } catch (err) {
        ClientLoggingService.log('ERROR', '[RawMarkdown] Error parsing markdown:', err);
        this.renderedHtml = '';
        // Fall back to raw view if parsing fails
        this.viewMode = 'raw';
      }
    } else {
      this.rawMarkdown = '';
      this.renderedHtml = '';
    }
  }
}
